<template>
    <div :class="[ 'salesforce-send-widget' ]">
        <div class="widget-header">
            <h3>Send To Salesforce</h3>
            <div class="svg-container widget-close-button" @click="closeWidget">
                <widget-close-svg />
            </div>
        </div>
        <div v-if="isInitializing">
            <loading-screen-svg />
        </div>
        <div v-else class="content-wrapper">
            <salesforce-send-contact v-if="contact" @selectCompany="selectCompany" @removeCompany="removeCompany" :contact="contact" :company="company"/>
        </div>
        <div class="widget-footer">
            <div class="cancel-container">
                <app-button class="transparent-btn" :onClick="closeWidget">
                     Cancel
                </app-button>
            </div>
            <div class="action-container">
                <app-button :onClick="sendContactToSalesforce" :loading="isSendingContactToSalesforce" :disabled="isSendingContactToSalesforce || deactivatedSwitches">
                    Send to Salesforce
                </app-button>
            </div>
        </div>
    </div>
</template>

<script>
import log, { LogLevel } from "@/scripts/logger";

const AppButton = () => import('@/components/common/app-button');
const LoadingScreenSvg = () => import('@/components/svg/loading-screen-svg');
const WidgetCloseSvg = () => import('@/components/svg/quick-add/widget-close-svg');
const SalesforceSendContact = () => import('@/components/integration/salesforce-send-contact');

import EventBus from '@/scripts/event-bus.js';

export default {
    name: 'salesforce-send-contact-widget',

    components: {
        'app-button': AppButton,
        'loading-screen-svg': LoadingScreenSvg,
        'widget-close-svg': WidgetCloseSvg,
        'salesforce-send-contact': SalesforceSendContact
    },

    props: {
        widget: {
            type: Object,
            required: true
        }
    },

    data() {
        return {
            isProcessing: true,
            contact: {
                id: null,
                name: null,
                profileImageUrl: null,
                shouldSendInformation: true,
                shouldSendOpportunities: false,
                shouldSendActivities: false,
                errorMessage: null,
                selectedOption: null,
                clientId: null,
                salesforceContactId: null,
            },
            company: {
                id: null,
                name: null,
                imageUrl: null,
                salesforceCompanyId: -1,
            },
            isSavingCompanyToContact: false,
            isSendingContactToSalesforce: false,
            deactivatedSwitches: false,
            salesforce: null,
            isInitializing: true
        }
    },

    computed: {
        salesforceUrl() {
            if (this.salesforce && this.contact.salesforceContactId) {
                return `${ this.salesforce.instanceUrl }/lightning/r/Contact/${this.contact.salesforceContactId}/view`;
            }
            else {
                return null;
            }
        },
    },

    watch: {
        'contact.shouldSendInformation': function() {
            this.deactivatedSwitches = !(this.contact.shouldSendInformation
                || this.contact.shouldSendOpportunities
                || this.contact.shouldSendActivities)
        },

        'contact.shouldSendOpportunities': function() {
            this.deactivatedSwitches = !(this.contact.shouldSendInformation
                || this.contact.shouldSendOpportunities
                || this.contact.shouldSendActivities)
        },

        'contact.shouldSendActivities': function() {
            this.deactivatedSwitches = !(this.contact.shouldSendInformation
                || this.contact.shouldSendOpportunities
                || this.contact.shouldSendActivities)
        }
    },

    created() {
        this.getData();
    },

    methods: {
        closeWidget() {
            EventBus.$emit('close-widget', this.widget);
        },

        getData() {
            this.getContact()
            .then(this.getIntegrationInformation)
            .catch((error) => {
                this.$helpers.showFailNotification(error, { error });
            });
        },

        getContact() {
            return new Promise((resolve, reject) => {
                this.$api
                    .get(`/service/Contacts?$filter=Id eq ${ this.widget.options.id }&$expand=Company`)
                    .then(response => {
                        this.createContact(response.data.value[0]);
    
                        if (response.data.value[0].Company) {
                            this.createCompany(response.data.value[0].Company);
                        }
    
                        resolve();
                    })
                    .catch((error) => {
                        log.error("Failed to fetch contact", error);
                        reject('An error occurred while fetching the contacts you selected.');
                });
            });
        },

        getIntegrationInformation() {
            return new Promise((resolve, reject) => {
                this.$api
                    .get(`/ms/salesforce-api/v1/credentials/${this.contact.clientId}`)
                    .then(response => {
                        if(response.data.data) {
                            this.salesforce = {
                                instanceUrl: response.data.data.SalesforceInstanceUrl
                            }
                         }
                        this.isInitializing = false;
                    })
                    .catch((error) => {
                        log.error("Failed to get SalesForce integration information", error);
                        reject('An error occurred while fetching your Salesforce integration info.');
                });
            });
        },

        createContact(contactsResponse) {
            this.contact.id = contactsResponse.Id;
            this.contact.name = this.$helpers.getName(contactsResponse.FirstName, contactsResponse.LastName);
            this.contact.profileImageUrl = contactsResponse.ProfileImageUrl || require('@/assets/images/avatar.png');
            this.contact.shouldSendInformation = true;
            this.contact.shouldSendOpportunities = false;
            this.contact.shouldSendActivities = false;
            this.contact.errorMessage = null;
            this.contact.selectedOption = contactsResponse.IsUpsert ? 'upsert' : 'insert';
            this.contact.clientId = contactsResponse.ClientId;
        },

        createCompany(company) {
            this.company.id = company.Id;
            this.company.name = company.Name;
            this.company.imageUrl = company.LogoUrl || require('@/assets/images/company-avatar.png');
            this.company.salesforceCompanyId = company.SalesforceCompanyId;
        },

        selectCompany(company) {
            this.company = {
                id: company.id,
                name: company.name,
                imageUrl: company.imageUrl
            }

            this.saveCompanyToContact();
        },

        removeCompany() {
            this.company = null;
        },

        openCreateCompanyWidget() {
            var widget = {
                name: 'quick-add-company-widget'
            };

            EventBus.$emit('open-widget', widget);
        },

        saveCompanyToContact() {
            this.isSavingCompanyToContact = true;

            var changes = [{
                'name': 'CompanyId',
                'value': this.company.id
            }];

            this.$api
                .patch(`/api/Contacts/${ this.widget.options.id }`, changes)
                .then(response => {
                    this.isSavingCompanyToContact = false;
                    this.$helpers.showSuccessNotification(`<strong>${ this.contact.name }</strong> has been added to ${ this.company.name }`);
                })
                .catch((error) => {
                    this.isSavingCompanyToContact = false;
                    this.$helpers.showFailNotification(`Unable to add ${this.contact.name} to ${this.company.name}.`,
                        { level: LogLevel.DEBUG }
                    );
                    log.error('Unable to add contact to company', error);
                });
        },

        sendContactToSalesforce() {
            this.isSendingContactToSalesforce = true;

            this.sendCompanyInfo()
            .then(this.sendContactInfo)
            .then(this.getSalesforceContactId)
            .then(() => {
                this.isSendingContactToSalesforce = false;
                this.contact.errorMessage = null;

                this.createSuccessMessage();

                EventBus.$emit('contact:update');
                this.closeWidget();
            })
            .catch(error => {
                log.error("Failed send contact to SalesForce", error);
                this.isSendingContactToSalesforce = false;
                this.contact.errorMessage = error.Message;
            });
        },

        getSalesforceContactId() {
            return new Promise((resolve, reject) => {
                this.$api
                    .get(`/service/Contacts?$filter=Id eq ${ this.contact.id } &$select=SalesforceContactId`)
                    .then(response => {
                        this.contact.salesforceContactId = response.data.value[0].SalesforceContactId;
                        resolve();
                    })
                    .catch((response) => {
                        log.error("Failed to get SalesForce contact ID", response);
                        reject(response.response.data);
                });
            });
        },

        createSuccessMessage() {
            var actionTaken = this.contact.isUpsert ? 'updated' : 'inserted';
            var message = `<strong>${ this.contact.name }</strong> has been ${ actionTaken } in Salesforce.`;
            var link = this.salesforceUrl;

            EventBus.$emit('show-notification', {
                iconUrl: require('@/assets/images/SVG/checkmark.svg'),
                isIconSvg: true,
                message: message,
                actionLabel: 'View',
                link: link,
                action: null
            });
        },

        sendCompanyInfo() {
            return new Promise((resolve, reject) => {
                if(this.company && !this.company.salesforceCompanyId) {
                    var request = {
                       isUpsert: false
                    };

                    this.$api
                        .post(`/ms/salesforce-api/v1/integrations/export/companies/${this.company.id}/${this.contact.clientId}`, request)
                        .then(response => {
                            this.$helpers.showSuccessNotification(`${ this.company.name } has been inserted in Salesforce`)
                            resolve();
                        })
                        .catch((response) => {
                            log.error("Failed to send company info to SalesForce", response);
                            reject(response.response.data);
                        });
                }
                else {
                    resolve();
                }
            });
        },
        
        sendContactInfo() {
            return new Promise((resolve, reject) => {
                var request = {
                    isUpsert: this.contact.isUpsert,
                    exportContacts: this.contact.shouldSendInformation,
                    exportDeals: this.contact.shouldSendOpportunities,
                    exportActivities: this.contact.shouldSendActivities,
                };

                this.$api
                    .post(`/ms/salesforce-api/v1/integrations/export/contacts/${this.contact.id}/${this.contact.clientId}`, request)
                    .then(response => {
                        resolve();
                    })
                    .catch((response) => {
                        log.error("Failed to send contact info to SalesForce", response);
                        reject(response.response.data);
                });
            })
        }
    },
}
</script>

<style>
.salesforce-send-widget .content-wrapper {
    padding-left: 0px;
    padding-right: 0px;
}

.salesforce-send-widget .btn.transparent-btn {
    background: transparent;
    color: #877D88;
}

.salesforce-send-widget .action-container .btn.transparent-btn {
    background: transparent;
    color: #18AEFF;
}

.salesforce-send-widget .action-container .btn.transparent-btn:hover {
    background: #DBEDFA;
}

.salesforce-send-widget .btn.transparent-btn:hover {
    background: #E1E0E5;
}

.salesforce-send-widget .company-options-container h4 {
    margin-bottom: 20px;
}

.salesforce-send-widget .widget-content .processing {
    display: block;
    width: 200px;
    margin: 0 auto;
}

.salesforce-send-widget .widget-content .row {
    margin-bottom: 30px;
}
</style>